import React, { useState, useEffect, useMemo, useRef } from "react";
import { CheckCircle, XCircle } from "lucide-react";
import "./Questionnaire.css";
import Logo from "../../../commonComponents/header/Logo";
import RenderTrueFalse from "./RenderTrueFalse";
import formatText from "../../../commonComponents/formatText/FormatText";
import MatchTheFollowing from "./MatchTheFollowing";

const QuestionnaireComponent = ({
  question,
  onAnswer,
  showResult = false,
  onPass,
}) => {
  const [selectedAnswers, setSelectedAnswers] = useState([]);
  const [connections, setConnections] = useState({});
  const [submitted, setSubmitted] = useState(false);
  const [showAnswer, setShowAnswer] = useState(false);
  const [showSuccessMessage, setShowSuccessMessage] = useState(false);
  const [leftItems, setLeftItems] = useState([]);
  const [rightItems, setRightItems] = useState([]);
  const [isSubmitting, setIsSubmitting] = useState(false);

  // Animation states
  const [isVisible, setIsVisible] = useState(false);
  const [isClosing, setIsClosing] = useState(false);
  const [successAnimation, setSuccessAnimation] = useState(false);

  // --- Timer state ---
  const TIMER_DURATION_MATCHING = 120; // seconds
  const TIMER_DURATION_DEFAULT = 60; // seconds
  const [timeLeft, setTimeLeft] = useState(TIMER_DURATION_DEFAULT);
  const [timedOut, setTimedOut] = useState(false);
  const timeoutHandledRef = useRef(false);

  // --- Pass button lock: disabled for 5s at the start of every question ---
  const PASS_ENABLE_DELAY = 4000; // ms
  const [passEnabled, setPassEnabled] = useState(false);

  // Initialize component with slide-in animation
  useEffect(() => {
    const timer = setTimeout(() => {
      setIsVisible(true);
    }, 50);
    return () => clearTimeout(timer);
  }, []);

  // Reset state when question changes
  useEffect(() => {
    setSelectedAnswers([]);
    setConnections({});
    setSubmitted(false);
    setShowAnswer(false);
    setShowSuccessMessage(false);
    setSuccessAnimation(false);
    setTimedOut(false);
    timeoutHandledRef.current = false;
    setTimeLeft(
      ["match-following", "matching"].includes(question?.type)
        ? TIMER_DURATION_MATCHING
        : TIMER_DURATION_DEFAULT,
    );

    // Lock the Pass button for this question; it unlocks after PASS_ENABLE_DELAY
    setPassEnabled(false);
    const passTimer = setTimeout(() => {
      setPassEnabled(true);
    }, PASS_ENABLE_DELAY);

    if (["match-following", "matching"].includes(question?.type)) {
      // Parse the title string for match-following questions
      if (question.title && question.title.includes(":")) {
        const [leftRaw, rightRaw] = question.title.split(":");

        // Split by comma and clean up whitespace
        const leftOptions = leftRaw
          .split(",")
          .map((s) => s.trim())
          .filter((s) => s.length > 0);
        const rightOptions = rightRaw
          .split(",")
          .map((s) => s.trim())
          .filter((s) => s.length > 0);

        // Shuffle the right options
        const shuffledRight = [...rightOptions].sort(() => Math.random() - 0.5);

        setLeftItems(leftOptions);
        setRightItems(shuffledRight);
      } else {
        // Fallback to existing structure
        setLeftItems(question.leftOptions || []);
        setRightItems(
          (question.rightOptions || []).sort(() => Math.random() - 0.5),
        );
      }
    }

    return () => {
      clearTimeout(passTimer);
    };
  }, [question]);

  const handleTimeout = () => {
    if (timeoutHandledRef.current) return;
    timeoutHandledRef.current = true;

    setTimedOut(true);

    // Emit peripheral event for the unanswered question
    window.dispatchEvent(
      new CustomEvent("no-answer-questionnaire", {
        detail: {
          questionId: question?.id,
          type: question?.type,
          timestamp: Date.now(),
        },
      }),
    );

    // Close the questionnaire the same way a normal close happens
    setIsClosing(true);
    setTimeout(() => {
      window.dispatchEvent(new CustomEvent("questionnaire-closed"));
    }, 400);
  };

  const handlePass = () => {
    if (timeoutHandledRef.current || submitted || !passEnabled) return;

    const shouldClose = onPass
      ? onPass({ questionId: question?.id, type: question?.type })
      : false;

    if (!shouldClose) return;

    timeoutHandledRef.current = true;

    window.dispatchEvent(
      new CustomEvent("mcq-passed", {
        detail: {
          questionId: question?.id,
          type: question?.type,
          timestamp: Date.now(),
        },
      }),
    );

    setIsClosing(true);
    setTimeout(() => {
      window.dispatchEvent(new CustomEvent("questionnaire-closed"));
    }, 400);
  };
  // Countdown timer — ticks once per second, stops once answered/timed out
  useEffect(() => {
    if (submitted || timedOut) return undefined;

    if (timeLeft <= 0) {
      handleTimeout();
      return undefined;
    }

    const id = setTimeout(() => {
      setTimeLeft((prev) => prev - 1);
    }, 1000);

    return () => clearTimeout(id);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [timeLeft, submitted, timedOut]);

  const handleSingleChoice = (optionIndex) => {
    setSelectedAnswers([optionIndex]);
  };

  const handleMultipleChoice = (optionIndex) => {
    setSelectedAnswers((prev) =>
      prev.includes(optionIndex)
        ? prev.filter((i) => i !== optionIndex)
        : [...prev, optionIndex],
    );
  };

  const handleTrueFalse = (answer) => {
    setSelectedAnswers([answer]);
  };

  // Handle submit with proper data formatting for API
  const handleSubmit = () => {
    if (timedOut) return;
    setSubmitted(true);
    setIsSubmitting(true); // Show loader first

    // Prepare data for the parent component and API
    let answersForParent = selectedAnswers;
    let draggedItemsForParent = null;

    // For match-following questions, pass the connected matching items
    if (question?.type === "match-following") {
      const matchedRightItems = leftItems.map((_, index) => {
        const leftId = `left-${index}`;
        const rightId = connections[leftId];
        if (rightId != null) {
          const rightIndex = parseInt(rightId.replace("right-", ""), 10);
          return rightItems[rightIndex] ?? "";
        }
        return rightItems[index] ?? "";
      });

      draggedItemsForParent = {
        leftItems: leftItems,
        rightItems: matchedRightItems,
      };
    }

    // Notify parent about the answer
    if (onAnswer) {
      onAnswer(answersForParent, draggedItemsForParent);
    }

    // Show loader for 2 seconds, then show success message
    setTimeout(() => {
      setIsSubmitting(false);
      setShowSuccessMessage(true);

      // Trigger success animation after a brief delay
      setTimeout(() => {
        setSuccessAnimation(true);
      }, 100);
    }, 2000);

    // Start closing animation after success message shows for 2.5 seconds
    setTimeout(() => {
      setIsClosing(true);
    }, 4500); // 2s loader + 2.5s success

    // Complete close after animation finishes
    setTimeout(() => {
      setShowSuccessMessage(false);
      // Dispatch event to close questionnaire
      window.dispatchEvent(new CustomEvent("questionnaire-closed"));
    }, 5000); // 2s loader + 3s success
  };

  const isAnswerCorrect = (optionIndex) => {
    if (!question.correctAnswers || !showAnswer) return null;
    return question.correctAnswers.includes(optionIndex);
  };

  // --- Single Choice ---
  const renderSingleChoice = () => (
    <div className="w-full flex">
      {/* Logo */}
      <div className="w-1/12 flex-shrink-0 flex items-start logo-adjutment">
        <Logo />
      </div>

      {/* Content */}
      <div className="w-11/12">
        {/* Header */}
        <div className="text-center mb-8">
          <h1 className="text-questionPrimaryFontSize mb-8 font-medium text-fontColor-new">
            A Quick Question! Read and Answer carefully.
          </h1>
          <p className="text-questionSecondaryFontSize mb-8 font-medium text-fontColor-new">
            For the statement below,{" "}
            <strong className="underline">Only One</strong> option is the right
            answer. Choose the correct option
          </p>
        </div>

        {/* Centered container with inline-block */}
        <div className="flex justify-center">
          <div className="inline-block text-left">
            {/* Question text */}
            <h2 className="text-questionSecondaryFontSize font-medium text-questionColor mb-6 text-center w-full">
              {question.title}
            </h2>

            {/* Options aligned to question start */}
            <div className="flex flex-col space-y-4">
              {question.options.map((option, index) => (
                <label
                  key={index}
                  className={`flex items-center p-4 rounded-lg cursor-pointer transition-all duration-200 ${
                    selectedAnswers.includes(index)
                      ? "bg-purple-50 border-purple-400"
                      : "border-gray-200"
                  }`}
                  onClick={() =>
                    !submitted && !timedOut && handleSingleChoice(index)
                  }
                >
                  <input
                    type="radio"
                    name="single-choice"
                    checked={selectedAnswers.includes(index)}
                    onChange={() =>
                      !submitted && !timedOut && handleSingleChoice(index)
                    }
                    className="w-6 h-6 accent-[#FFC857] mr-4"
                  />
                  <span className="text-fontColor-new font-medium text-questionSecondaryFontSize">
                    {option}
                  </span>
                </label>
              ))}
            </div>
          </div>
        </div>
      </div>
    </div>
  );

  // --- Multiple Choice ---
  const renderMultipleChoice = () => (
    <div className="w-full flex">
      {/* Logo */}
      <div className="w-1/12 flex-shrink-0 flex items-start logo-adjutment">
        <Logo />
      </div>

      {/* Content */}
      <div className="w-11/12">
        {/* Header */}
        <div className="text-center mb-8">
          <h1 className="text-questionPrimaryFontSize mb-8 font-medium text-fontColor-new">
            A Quick Question! Read and Answer carefully.
          </h1>
          <p className="text-questionSecondaryFontSize mb-8 font-medium text-fontColor-new">
            For the statement below,{" "}
            <strong className="underline">Multiple</strong> options can be the
            right answer. Choose all correct options
          </p>
        </div>

        {/* Centered container with inline-block */}
        <div className="flex justify-center">
          <div className="inline-block text-left">
            {/* Question text */}
            <h2 className="text-questionSecondaryFontSize font-medium text-questionColor mb-6 text-center w-full">
              {question.title}
            </h2>

            {/* Options aligned to question start */}
            <div className="flex flex-col space-y-4">
              {question.options.map((option, index) => (
                <label
                  key={index}
                  className={`flex items-center p-4 rounded-lg cursor-pointer transition-all duration-200 ${
                    selectedAnswers.includes(index)
                      ? "bg-purple-50 border-purple-400"
                      : "border-gray-200"
                  }`}
                >
                  <input
                    type="checkbox"
                    checked={selectedAnswers.includes(index)}
                    onChange={() =>
                      !submitted && !timedOut && handleMultipleChoice(index)
                    }
                    className="w-6 h-6 accent-[#FFC857] mr-4"
                  />
                  <span className="text-fontColor-new font-medium text-questionSecondaryFontSize capitalize">
                    {option}
                  </span>
                </label>
              ))}
            </div>
          </div>
        </div>
      </div>
    </div>
  );

  // --- True / False ---
  const renderTrueFalse = () => (
    <div className="w-full flex">
      {/* Logo */}
      <div className="w-1/12 flex-shrink-0 flex items-start logo-adjutment">
        <Logo />
      </div>

      {/* Content */}
      <div className="w-11/12">
        {/* Header */}
        <div className="text-center mb-8">
          <h1 className="text-questionPrimaryFontSize font-medium mb-8 text-fontColor-new">
            A Quick Question! Read and Answer carefully.
          </h1>
          <p className="text-questionSecondaryFontSize mb-8 font-medium text-fontColor-new">
            For the statement below, choose either True or False
          </p>
        </div>

        {/* Center container */}
        <div className="flex justify-center">
          {/* Inline-block ensures question + options share alignment */}
          <div className="inline-block text-left">
            {/* Question */}
            <h2 className="text-questionSecondaryFontSize font-medium text-questionColor mb-6 text-center w-full">
              {question.title}
            </h2>

            {/* Options aligned with question start */}
            <div className="flex flex-col space-y-4">
              {["True", "False"].map((option, index) => (
                <label
                  key={option}
                  className="flex items-center space-x-3 cursor-pointer"
                  onClick={() =>
                    !submitted && !timedOut && handleTrueFalse(index)
                  }
                >
                  <input
                    type="radio"
                    name="true-false"
                    value={option}
                    checked={selectedAnswers.includes(index)}
                    onChange={() =>
                      !submitted && !timedOut && handleTrueFalse(index)
                    }
                    className="form-radio h-5 w-5 accent-[#FFC857] border-gray-300 flex-shrink-0"
                  />
                  <span className="text-questionSecondaryFontSize font-medium text-fontColor-new">
                    {option}
                  </span>
                </label>
              ))}
            </div>
          </div>
        </div>
      </div>
    </div>
  );

  const formattedLeft = useMemo(() => {
    return leftItems.map((item, index) => ({
      id: `left-${index}`,
      label: formatText(item),
    }));
  }, [leftItems]);

  const formattedRight = useMemo(() => {
    return rightItems.map((item, index) => ({
      id: `right-${index}`,
      label: formatText(item),
    }));
  }, [rightItems]);

  // ✅ Match the Following
  const renderMatchFollowing = () => (
    <MatchTheFollowing
      title="A Quick Question! Read and Answer carefully."
      subtitle="Drag the arrow on each term to its matching definition on the right. Each term pairs with exactly one."
      context={
        question?.context ||
        "Context: Software Design and Patterns - Microservices"
      }
      left={formattedLeft}
      right={formattedRight}
      value={connections}
      onChange={setConnections}
      onSubmit={handleSubmit}
      disabled={isSubmitting || submitted || timedOut}
    />
  );

  const renderQuestion = () => {
    switch (question.type) {
      case "single-choice":
        return renderSingleChoice();
      case "multiple-choice":
        return renderMultipleChoice();
      case "true-false":
        return renderTrueFalse();
      case "match-following":
        return renderMatchFollowing();

      default:
        return <div>Unknown question type</div>;
    }
  };

  const canSubmit = () => {
    if (timedOut) return false;
    if (question.type === "match-following") {
      return Object.keys(connections).length === formattedLeft.length;
    }
    return selectedAnswers.length > 0;
  };

  // In QuestionnaireComponent.jsx, update the return statement:

  // In QuestionnaireComponent.jsx, update the return statement:

  return (
    <>
      <style jsx>{`
        /* ── Drag-and-Drop Animations ───────────────────────────── */

        /* Base drag item */
        .drag-item {
          transition:
            transform 0.18s cubic-bezier(0.34, 1.56, 0.64, 1),
            box-shadow 0.18s ease,
            opacity 0.18s ease,
            border-color 0.18s ease,
            background-color 0.18s ease;
          will-change: transform;
          user-select: none;
        }

        /* Item being dragged — lift + ghost */
        .drag-item--dragging {
          opacity: 0.38;
          transform: scale(0.97);
          box-shadow: none !important;
          filter: blur(0.5px);
        }

        /* Target slot while hovering over it */
        .drag-item--over {
          transform: scale(1.04);
          border-color: #ffc857 !important;
          box-shadow:
            0 0 0 2px rgba(255, 200, 87, 0.55),
            0 8px 24px rgba(255, 200, 87, 0.35) !important;
          background-color: rgba(255, 200, 87, 0.15) !important;
          z-index: 2;
        }

        /* Scale-up → settle bounce on drop */
        @keyframes settle {
          0% {
            transform: scale(1);
          }
          30% {
            transform: scale(1.1);
            box-shadow:
              0 0 0 3px #ffc85766,
              0 12px 32px #ffc85744;
            border-color: #ffc857 !important;
          }
          60% {
            transform: scale(0.96);
          }
          80% {
            transform: scale(1.03);
          }
          100% {
            transform: scale(1);
            box-shadow: none;
          }
        }

        .drag-item--settle {
          animation: settle 0.55s cubic-bezier(0.34, 1.56, 0.64, 1) forwards;
        }

        /* ── Timer badge ─────────────────────────────────────────── */
        .questionnaire-timer {
          transition:
            color 0.2s ease,
            border-color 0.2s ease,
            background-color 0.2s ease;
        }

        .questionnaire-timer--warning {
          animation: timerPulse 1s ease-in-out infinite;
        }

        @keyframes timerPulse {
          0%,
          100% {
            opacity: 1;
          }
          50% {
            opacity: 0.55;
          }
        }

        /* ── Existing modal / success animations ────────────────── */
        .modal-overlay {
          transition: all 0.3s cubic-bezier(0.4, 0, 0.2, 1);
          background-color: rgba(0, 0, 0, 0.8) !important;
        }

        .modal-content {
          transition: all 0.4s cubic-bezier(0.4, 0, 0.2, 1);
          transform: translateY(100px);
          opacity: 0;
          background-color: rgba(17, 23, 41, 0.8) !important;
        }

        .modal-content.visible {
          transform: translateY(0);
          opacity: 1;
        }

        .modal-content.closing {
          transform: translateY(-100px);
          opacity: 0;
        }

        .success-content {
          transition: all 0.5s cubic-bezier(0.4, 0, 0.2, 1);
          transform: translateY(50px);
          opacity: 0;
        }

        .success-content.animate {
          transform: translateY(0);
          opacity: 1;
        }

        .success-content.closing {
          transform: translateY(-50px);
          opacity: 0;
        }

        @keyframes checkBounce {
          0% {
            transform: scale(0) rotate(0deg);
          }
          50% {
            transform: scale(1.2) rotate(180deg);
          }
          100% {
            transform: scale(1) rotate(360deg);
          }
        }

        .progress-bar {
          animation: fillProgress 2s ease-out forwards;
        }

        @keyframes fillProgress {
          from {
            width: 0%;
          }
          to {
            width: 100%;
          }
        }

        /* Segmented animated loader */
        .loader {
          width: 60px;
          height: 60px;
          position: relative;
          animation: rotate 1s infinite linear;
        }

        .loader::before,
        .loader::after {
          content: "";
          position: absolute;
          top: 0;
          left: 0;
          width: 100%;
          height: 100%;
          border-radius: 50%;
          background: conic-gradient(
            transparent 0deg,
            transparent 270deg,
            #ffffff 270deg
          );
        }

        .loader::after {
          background: conic-gradient(
            transparent 0deg,
            transparent 90deg,
            rgba(255, 255, 255, 0.3) 90deg
          );
        }

        @keyframes rotate {
          from {
            transform: rotate(0deg);
          }
          to {
            transform: rotate(360deg);
          }
        }
      `}</style>

      <div
        className={`fixed inset-0 modal-overlay ${
          isVisible ? "bg-opacity-95" : "bg-opacity-0"
        } flex items-center justify-center z-50`}
        style={{ backgroundColor: "rgba(0, 0, 0, 0.95)" }}
      >
        <div
          className={`w-full h-full flex flex-col modal-content relative ${
            isVisible ? "visible" : ""
          } ${isClosing ? "closing" : ""}`}
          style={{
            backgroundColor: "#111729",
            backdropFilter: "blur(10px)",
          }}
        >
          {!submitted && !showSuccessMessage && (
            <div className="absolute top-3 right-5 mb-4 z-20 ">
              <div
                className={`flex items-center gap-2 px-5 py-1.5 rounded-full border backdrop-blur-sm transition-all duration-300 ${
                  timeLeft <= 10
                    ? "bg-[#1E293B] border-red-500/60 shadow-[0_0_20px_rgba(239,68,68,0.20)]"
                    : "bg-[#1E293B] border-gray-600/50 hover:border-[#FFC857]/60 shadow-[0_0_15px_rgba(255,200,87,0.10)]"
                }`}
              >
                <svg
                  className={`w-4 h-4 ${
                    timeLeft <= 10
                      ? "text-red-400 animate-pulse"
                      : "text-[#FFC857]"
                  }`}
                  fill="none"
                  stroke="currentColor"
                  viewBox="0 0 24 24"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth="2"
                    d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z"
                  />
                </svg>

                <span
                  className={`text-[10px] uppercase tracking-[0.18em] font-medium ${
                    timeLeft <= 10 ? "text-red-300" : "text-gray-300"
                  }`}
                >
                  Closes In
                </span>

                <span
                  className={`w-10 text-center font-mono font-extrabold text-base tracking-wide tabular-nums ${
                    timeLeft <= 10 ? "text-red-300" : "text-[#FFC857]"
                  }`}
                >
                  {timeLeft}s
                </span>
              </div>
            </div>
          )}
          {!showSuccessMessage && !isSubmitting ? (
            <>
              <div className="flex-1 overflow-y-auto p-5 mt-5">
                {renderQuestion()}
              </div>
              {!submitted && (
                <div className="flex flex-col w-full bg-transparent relative z-10">
                  {!timedOut && (
                    <div className="w-full flex justify-end px-6 pb-4 -mt-2">
                      <button
                        onClick={handlePass}
                        disabled={!passEnabled}
                        className={`group flex items-center gap-2 px-6 py-2 rounded-full shadow-[0_0_15px_rgba(255,200,87,0.1)] transition-all duration-300 ${
                          passEnabled
                            ? "bg-[#1E293B] border border-gray-600/50 hover:border-[#FFC857]/60 hover:shadow-[0_0_20px_rgba(255,200,87,0.2)] cursor-pointer"
                            : "bg-[#1E293B]/60 border border-gray-700/40 cursor-not-allowed opacity-60"
                        }`}
                      >
                        <span
                          className={`font-medium tracking-widest text-sm uppercase transition-colors ${
                            passEnabled
                              ? "text-gray-300 group-hover:text-[#FFC857]"
                              : "text-gray-500"
                          }`}
                        >
                          Pass
                        </span>
                        <svg
                          className={`w-4 h-4 transition-transform duration-300 ${
                            passEnabled
                              ? "text-[#FFC857] group-hover:translate-x-1"
                              : "text-gray-500"
                          }`}
                          fill="none"
                          stroke="currentColor"
                          viewBox="0 0 24 24"
                        >
                          <path
                            strokeLinecap="round"
                            strokeLinejoin="round"
                            strokeWidth="2"
                            d="M13 5l7 7-7 7M5 5l7 7-7 7"
                          />
                        </svg>
                      </button>
                    </div>
                  )}
                  <div className="border-gray-200 bg-white rounded-b-xl shadow-[0_-4px_6px_-1px_rgba(0,0,0,0.1)] z-20">
                    <button
                      onClick={handleSubmit}
                      disabled={!canSubmit()}
                      className={`w-full py-4 mtf-submit rounded-none font-semibold text-questionTertiaryFontSize transition-all duration-200 ${
                        canSubmit()
                          ? "bg-purple-750 text-white shadow-lg hover:shadow-xl"
                          : "bg-gray-300 text-fontColor cursor-not-allowed"
                      }`}
                    >
                      Submit Answer
                    </button>
                  </div>
                </div>
              )}
            </>
          ) : isSubmitting ? (
            // Loading state
            <div className="flex items-center justify-center h-full">
              <div className="flex flex-col items-center justify-center text-center space-y-6">
                {/* Bar-style spinner */}
                <div className="relative w-12 h-12 animate-spin-slow">
                  {[...Array(12)].map((_, i) => (
                    <span
                      key={i}
                      className="absolute left-1/2 top-1/2 block w-1 h-2.5 bg-[#FFF] rounded animate-spinner-bar"
                      style={{
                        transform: `rotate(${i * 30}deg) translate(0, -150%)`,
                        animationDelay: `${i * 0.1}s`,
                      }}
                    />
                  ))}
                </div>

                {/* Text below spinner */}
                <p className="text-questionSecondaryFontSize font-medium text-white">
                  Submitting your response...
                </p>
              </div>
            </div>
          ) : (
            // Success message (existing code)
            <div
              className={`flex items-center justify-center h-full success-content ${
                successAnimation ? "animate" : ""
              } ${isClosing ? "closing" : ""}`}
            >
              <div className="text-center">
                <h3 className="text-questionSecondaryFontSize font-semibold text-fontColor-new">
                  Your response was submitted. Taking you back...
                </h3>
              </div>
            </div>
          )}
        </div>
      </div>
    </>
  );
};

export default QuestionnaireComponent;
